import { useEffect, useRef, useState } from 'react';
import { Animated, Easing, View, Text, Pressable, ScrollView, AccessibilityInfo, Platform } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { BlurView } from 'expo-blur';
import Unicon from '@/components/General/Unicon';

export default function GuideCard({ step, index, total, onNext, onBack, onSkip, interactive, style, onLayout, onContentSizeChange, onTransitionPhase, measuring, moving = false, animate = true, blur = true }) {
    const { theme: { colors, scheme } } = useUnistyles();
    const nextRef = useRef(null);
    const timing = useRef(onTransitionPhase);
    timing.current = onTransitionPhase;
    const copyOpacity = useRef(new Animated.Value(1)).current;
    const [shown, setShown] = useState({ step, index, total, interactive });
    const [transitioning, setTransitioning] = useState(false);
    const stale = shown.step.featureID !== step.featureID || shown.step.learningRevision !== step.learningRevision || shown.index !== index;
    const disabled = measuring || moving || transitioning || stale;
    useEffect(() => {
        if (disabled) return;
        AccessibilityInfo.announceForAccessibility(`${shown.step.title}. ${shown.step.body}. Step ${shown.index + 1} of ${shown.total}.`);
        if (Platform.OS === 'web') nextRef.current?.focus?.();
    }, [shown.step.featureID, shown.step.title, shown.step.body, shown.index, shown.total, disabled]);
    // Fade old copy out before swapping content, keeping the previous step
    // and its dimensions intact while the next target is measured.
    useEffect(() => {
        if (measuring) return;
        const next = { step, index, total, interactive };
        if (!animate || !stale) {
            setShown(next);
            setTransitioning(false);
            copyOpacity.setValue(1);
            timing.current?.('copy ready without fade');
            return;
        }
        let live = true;
        let incoming;
        setTransitioning(true);
        timing.current?.('copy fade-out start');
        const outgoing = Animated.timing(copyOpacity, { toValue: 0, duration: 100, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== 'web' });
        outgoing.start(({ finished }) => {
            if (!live || !finished) return;
            setShown(next);
            timing.current?.('copy swapped');
            incoming = Animated.timing(copyOpacity, { toValue: 1, duration: 200, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== 'web' });
            incoming.start(({ finished }) => { if (live && finished) { setTransitioning(false); timing.current?.('copy ready'); } });
        });
        return () => { live = false; outgoing.stop(); incoming?.stop(); };
    }, [step.featureID, step.learningRevision, step.title, step.body, index, total, interactive, measuring, animate, copyOpacity]);
    const last = shown.index === shown.total - 1;
    return (
        <View testID="guide-card" style={[styles.card, style]} onLayout={onLayout} accessibilityViewIsModal aria-modal={Platform.OS === 'web' ? true : undefined} role={Platform.OS === 'web' ? 'dialog' : undefined} aria-label={shown.step.title}>
            {blur && <BlurView intensity={80} tint={scheme} style={StyleSheet.absoluteFill} />}
            <View style={[StyleSheet.absoluteFill, styles.tint(colors, scheme)]} />
            <ScrollView style={styles.scroll} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled" onContentSizeChange={onContentSizeChange}>
                <View style={styles.topRow}>
                    <Text style={styles.eyebrow}>QUICK GUIDE · {shown.index + 1} OF {shown.total}</Text>
                    <Pressable testID="guide-skip" accessibilityRole="button" onPress={onSkip} hitSlop={8}><Text style={styles.secondary}>Skip tour</Text></Pressable>
                </View>
                <Animated.View style={[styles.copy, { opacity: copyOpacity }]}>
                    <Text testID="guide-title" style={styles.title} accessibilityRole="header" accessibilityLiveRegion="polite">{shown.step.title}</Text>
                    <Text style={styles.body}>{shown.step.body}</Text>
                </Animated.View>
                <View style={styles.actions}>
                    <Pressable testID="guide-back" accessibilityRole="button" accessibilityLabel="Back" disabled={shown.index === 0 || disabled} accessibilityState={{ disabled: shown.index === 0 || disabled }} onPress={onBack} style={styles.nav} hitSlop={4}>
                        <Unicon name="chevron-back" size={16} colorVariant={shown.index === 0 ? 'textTertiary' : 'primary'} />
                        <Text style={[styles.navText, shown.index === 0 && styles.navOff]}>Back</Text>
                    </Pressable>
                    {shown.interactive ? (
                        <View style={styles.tapHint}><Unicon name="hand-left-outline" size={15} colorVariant="primary" /><Text style={styles.tapHintText}>Tap the highlighted control</Text></View>
                    ) : (
                        <Pressable ref={nextRef} testID="guide-next" disabled={disabled} accessibilityState={{ disabled }} accessibilityRole="button" accessibilityLabel={last ? 'Done' : 'Next'} onPress={onNext} style={styles.nav} hitSlop={4}>
                            <Text style={styles.navText}>{last ? 'Done' : 'Next'}</Text>
                            <Unicon name={last ? 'checkmark' : 'chevron-forward'} size={16} colorVariant="primary" />
                        </Pressable>
                    )}
                </View>
                {shown.index === 0 && <Text testID="guide-auto-note" style={styles.note}>Automatic walkthroughs can be turned off in Settings.</Text>}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness, shadows }) => ({
    card: { backgroundColor: 'transparent', borderRadius: roundness.lg, boxShadow: shadows.elevated, overflow: 'hidden', maxHeight: '90%', width: '100%' },
    tint: (colors, scheme) => ({ backgroundColor: colors.surface, opacity: scheme === 'dark' ? 0.8 : 0.88, borderRadius: roundness.lg }),
    scroll: { flexGrow: 0 },
    content: { padding: spacing.md, gap: spacing.sm },
    topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
    eyebrow: { color: colors.textSecondary, fontFamily: fonts.bold, fontSize: typography.micro },
    secondary: { color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.caption },
    copy: { gap: spacing.sm },
    title: { color: colors.text, fontFamily: fonts.bold, fontSize: typography.h2 },
    body: { color: colors.textSecondary, fontFamily: fonts.body, fontSize: typography.body, lineHeight: 23 },
    note: { color: colors.textTertiary, fontFamily: fonts.body, fontSize: typography.micro, lineHeight: 15, textAlign: 'center' },
    actions: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: spacing.sm },
    nav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2, minWidth: 92, minHeight: 40, paddingHorizontal: spacing.sm, borderRadius: roundness.sm },
    navText: { color: colors.primary, fontFamily: fonts.bold, fontSize: typography.body },
    navOff: { color: colors.textTertiary },
    tapHint: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, minWidth: 92, minHeight: 40 },
    tapHintText: { color: colors.primary, fontFamily: fonts.body, fontSize: typography.caption },
}));
